import { initializeApp } from 'firebase/app'
import { Auth, FacebookAuthProvider, getAuth, GoogleAuthProvider, signInWithPopup, signOut, User } from 'firebase/auth'
import { addDoc, collection, Firestore, getDocs, getFirestore, query, where } from 'firebase/firestore'
import { createContext, FC, useCallback, useContext, useMemo } from 'react'
import { useAuthState } from 'react-firebase-hooks/auth'

import useLogin from '../hooks/useLogin'
import { IFirebaseConfig } from '../redux/configuration'

interface IFirebaseContext {
  auth: Auth
  db: Firestore
  user?: User | null
  isFirebaseLoading: boolean
  logout: () => Promise<void>
  signInWithGoogle: () => Promise<void>
  signInWithFacebook: () => Promise<void>
}

const FirebaseContext = createContext<IFirebaseContext>({} as IFirebaseContext)

interface IFirebaseProvider {
  firebaseConfig: IFirebaseConfig
}

const FirebaseProvider: FC<IFirebaseProvider> = ({ firebaseConfig, children }) => {
  const app = initializeApp(firebaseConfig)
  const auth = getAuth(app)
  const db = getFirestore(app)

  const { updateSocialToken } = useLogin()

  const [googleProvider, facebookProvider] = useMemo(() => {
    const googleProvider = new GoogleAuthProvider()
    const facebookProvider = new FacebookAuthProvider()

    return [googleProvider, facebookProvider]
  }, [])

  const [user, isFirebaseLoading] = useAuthState(auth, {
    onUserChanged: async (user) => {
      if (user) {
        const { token } = await user.getIdTokenResult()
        updateSocialToken(token)
      }
    },
  })

  const signInWithGoogle = useCallback(async () => {
    try {
      const res = await signInWithPopup(auth, googleProvider)
      const { user } = res

      const q = query(collection(db, 'users'), where('uid', '==', user.uid))
      const docs = await getDocs(q)
      if (docs.docs.length === 0) {
        await addDoc(collection(db, 'users'), {
          uid: user.uid,
          name: user.displayName,
          authProvider: 'google',
          email: user.email,
        })
      }
    } catch (err) {
      console.error(err)
    }
  }, [auth, googleProvider, db])

  const signInWithFacebook = useCallback(async () => {
    try {
      const res = await signInWithPopup(auth, facebookProvider)
      const { user } = res

      const q = query(collection(db, 'users'), where('uid', '==', user.uid))
      const docs = await getDocs(q)
      if (docs.docs.length === 0) {
        await addDoc(collection(db, 'users'), {
          uid: user.uid,
          name: user.displayName,
          authProvider: 'facebook',
          email: user.email,
        })
      }
    } catch (err) {
      console.error(err)
    }
  }, [auth, facebookProvider, db])

  const logout = useCallback(async () => {
    await signOut(auth)
    updateSocialToken('')
  }, [auth])

  return (
    <FirebaseContext.Provider
      value={{
        db,
        auth,
        user,
        logout,
        signInWithGoogle,
        isFirebaseLoading,
        signInWithFacebook,
      }}
    >
      {children}
    </FirebaseContext.Provider>
  )
}

export const useFirebase = () => useContext(FirebaseContext)

export default FirebaseProvider
